import { Component } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { ActivatedRoute, Router } from '@angular/router';
import { Button } from '../../../components/common/button/button';
import { AuthApiService } from '../../../services/apis/auth-api.service';
import { ApiException, ApiStatus } from '../../../services/apis/status';
import { MessageService } from '../../../services/message.service';
import { TitleService } from '../../../services/title.service';
import { validateFormGroup } from '../../../utils/form';

@Component({
  selector: 'app-reset-password-page',
  imports: [
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    Button,
    MatIconModule,
    ReactiveFormsModule,
  ],
  templateUrl: './reset-password-page.html',
  styleUrl: './reset-password-page.scss',
})
export class ResetPasswordPage {
  token: string = '';
  nonce: string = '';
  formGroup: FormGroup;
  loading: boolean = false;
  constructor(
    private rotue: ActivatedRoute,
    private fb: FormBuilder,
    private titleService: TitleService,
    private authApi: AuthApiService,
    private messageService: MessageService,
    private router: Router
  ) {
    this.titleService.setTitle('Reset Password');
    this.formGroup = this.fb.group({
      password: [
        '',
        [Validators.required, Validators.pattern(`^(?=.*[A-Z]).{8,}$`)],
      ],
      password_confirmation: [
        '',
        [Validators.required, this.passwordMatchValidator],
      ],
    });
    this.rotue.queryParams.subscribe((params) => {
      this.token = params['token'] ?? '';
      this.nonce = params['nonce'] ?? '';
    });
  }

  passwordMatchValidator = (
    control: AbstractControl
  ): ValidationErrors | null => {
    if (!this.formGroup) {
      return null;
    }
    const password = this.formGroup.controls['password']?.value;
    const passwordConfirmation =
      this.formGroup.controls['password_confirmation']?.value;
    return password === passwordConfirmation
      ? null
      : { passwordMismatch: true };
  };

  async submit(e: SubmitEvent) {
    const values = validateFormGroup(this.formGroup);
    if (!values) {
      return;
    }
    try {
      this.loading = true;
      const r = await this.authApi.resetPassword({
        token: this.token,
        nonce: this.nonce,
        password: values.password,
      });
      this.messageService.open('Password reset successfully', 'OK');
      this.router.navigate(['/login'], { replaceUrl: true });
    } catch (error) {
      if (error instanceof ApiException) {
        if (error.status === ApiStatus.USER_EMAIL_TOKEN_NOT_FOUND) {
          this.messageService.open('Invalid token', 'OK');
        } else if (error.status === ApiStatus.USER_EMAIL_TOKEN_EXPIRED) {
          this.messageService.open('Link expired', 'OK');
        } else if (error.status === ApiStatus.USER_EMAIL_TOKEN_USED) {
          this.messageService.open('Link used', 'OK');
        }
      }
    } finally {
      this.loading = false;
    }
  }
}
